import { FC, useEffect, useMemo, useState } from 'react';
import { SupportSubjectListResponse } from '@restore/SupportSubject/SupportSubject.types';
import { SupportSubjectType } from '@dynamika-shared/types/subjects';
import { makeSupportSubjectListColumns } from './SupportSubjectListTable.columns';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { Input } from 'antd';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { SupportSubjectListHeader } from './SupportSubjectListHeader';

type SupportSubjectListPageViewProp = {
  data?: Partial<SupportSubjectListResponse>;
  loading?: boolean;
  onSearch: (value: string) => void;
  onSubjectClick: (subject: SupportSubjectType) => void;
  onSort: (name: string, dir?: string) => void;
  sortState?: string;
};

export const SupportSubjectListView: FC<SupportSubjectListPageViewProp> = ({
  data,
  loading,
  onSearch,
  onSubjectClick,
  onSort,
  sortState,
}) => {
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue('');
    onSearch('');
  }, [onSearch]);

  const supportSubjectListColumns = useMemo(
    () => makeSupportSubjectListColumns({ onSubjectClick }),
    [onSubjectClick]
  );

  const totalElements = data?.totalElements ?? 0;
  const pagination = useMakePagination('subjectList', !!data?.totalElements);

  const handleClearInput = () => {
    setSearchValue('');
    onSearch('');
  };

  return (
    <>
      <div className='flex justify-between pb-3 pt-[13px]'>
        <SupportSubjectListHeader totalElements={totalElements} />

        <div className='flex items-center'>
          <Input
            placeholder='Search Subject ID...'
            className='min-w-[400px] max-w-lg rounded-[32px] px-2 py-[10px] text-1424 text-spanishGray'
            style={{ boxShadow: 'none' }}
            suffix={
              <SearchSuffix action={handleClearInput} disabled={!searchValue} />
            }
            value={searchValue}
            onChange={({ target: { value } }) => {
              const upperCaseValue = value.toUpperCase();
              setSearchValue(upperCaseValue);
              onSearch(upperCaseValue);
            }}
          />
        </div>
      </div>

      <Table
        columns={supportSubjectListColumns}
        data={data?.content || []}
        totalItems={data?.totalElements ?? 0}
        loading={loading}
        loadingLength={8}
        pagination={pagination}
        onSort={onSort}
        sortState={sortState}
      />
    </>
  );
};
